import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { userAtom } from '../state/userAtom'
import { useAtom } from 'jotai'
import { useAuth } from '../hooks/useAuth'
import { LinearProgress } from '@mui/material'
import { UserRole } from '../types/userRoles'
import { SubscriptionStatus } from '../types/userDetails.types'
import { MemberRoutes } from '../config/routes.config'

interface DeactivatedUserRouteProps {
  children: React.ReactElement
  allowedRole: UserRole
}

/**
 * Route protection component that handles deactivated users.
 * Deactivated users can only access account routes (/account/*).
 * All other routes redirect to account profile page.
 */
const DeactivatedUserRoute: React.FC<DeactivatedUserRouteProps> = ({ children, allowedRole }) => {
  const { loading } = useAuth()
  const [user] = useAtom(userAtom)
  const location = useLocation()

  if (loading) {
    return <LinearProgress sx={{ height: '5px' }} />
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ returnTo: location.pathname + location.search }} />
  }

  if (user.role !== allowedRole) {
    return <Navigate to="/" />
  }

  // Check if user is deactivated
  const isDeactivated = user.subscriptionStatus === SubscriptionStatus.DEACTIVATED

  if (isDeactivated) {
    // Allow access to account routes only
    const isAccountRoute = location.pathname.startsWith('/account')

    if (!isAccountRoute) {
      // Redirect deactivated users to their account subscription page
      return <Navigate to={MemberRoutes.ACCOUNT_SUBSCRIPTION} replace />
    }
  }

  return <>{children}</>
}

export default DeactivatedUserRoute
